/**
 * Port of the two lookup tables defined inline inside
 * `app/actionAudit/actionAuditController.js` (`ACTIONS_CONSTANTS` and
 * `ENTITY_TYPE_CONSTANTS`).
 *
 * `ACTIONS_CONSTANTS` maps a raw `logEntityAction` value (as sent to/received
 * from `POST {urlAstrea}/api/v2/logs/query`) to its Portuguese display label.
 * `ENTITY_TYPE_CONSTANTS` maps a raw `entityType` value to both its display
 * label and the subset of `ACTIONS_CONSTANTS` keys that are valid for that
 * entity (used to populate the dependent "Ações" `<select>` once an
 * "Entidade" is chosen — legacy `onChangeEntityType()`).
 *
 * Kept as a standalone module (not inlined in the page component) so the
 * page and its test can both import the same constant map without
 * redefining it, mirroring how `astreaEnvironments.js` factors out
 * `authService.js`'s environment map.
 */
export const ACTIONS_CONSTANTS = {
  CREATE: 'Criação',
  UPDATE: 'Atualização',
  DISCARD: 'Descarte',
  DELETE: 'Exclusão',
  TREAT: 'Tratamento',
  MERGED_TO: 'Mergeado para (Mesclado para)',
  MERGED_FROM: 'Mergeado de (Mesclado de)',
  LINK: 'Vinculado',
  UNLINK: 'Desvinculado',
  ACTIVE: 'Ativado',
  INACTIVE: 'Inativado',
  UNDO: 'Desfazer Tratamento',
  RESET: 'Marcado como Não tratada',
  ENABLE: 'Habilitado',
  DISABLE: 'Desabilitado',
}

export const ENTITY_TYPE_CONSTANTS = {
  Alert: {
    value: 'Alert',
    name: 'Alerta',
    actions: ['DISCARD'],
  },
  Historical: {
    value: 'Historical',
    name: 'Andamento',
    actions: ['DELETE'],
  },
  Hearing: {
    value: 'Hearing',
    name: 'Audiência',
    actions: ['DELETE'],
  },
  AstreaAppointment: {
    value: 'AstreaAppointment',
    name: 'Compromisso',
    actions: ['DELETE'],
  },
  Contact: {
    value: 'Contact',
    name: 'Contato',
    actions: ['MERGED_TO', 'MERGED_FROM'],
  },
  ClippingSearch: {
    value: 'ClippingSearch',
    name: 'Nome de publicação',
    actions: ['DELETE', 'LINK'],
  },
  CourtSubpoenaSearch: {
    value: 'CourtSubpoenaSearch',
    name: 'Nome de Intimação',
    actions: ['DELETE'],
  },
  Deadline: {
    value: 'Deadline',
    name: 'Prazo',
    actions: ['DELETE'],
  },
  Case: {
    value: 'Case',
    name: 'Processo',
    actions: ['DELETE', 'UNLINK', 'LINK'],
  },
  Clipping: {
    value: 'Clipping',
    name: 'Publicação',
    actions: ['DISCARD', 'TREAT', 'DELETE', 'UNDO', 'RESET'],
  },
  Task: {
    value: 'Task',
    name: 'Tarefa',
    actions: ['DISCARD', 'DELETE'],
  },
  User: {
    value: 'User',
    name: 'Usuário',
    actions: ['ACTIVE', 'INACTIVE'],
  },
  TENANT_TWO_FACTOR_POLICY: {
    value: 'TENANT_TWO_FACTOR_POLICY',
    name: 'Autenticação de dois fatores',
    actions: ['ENABLE', 'DISABLE'],
  },
  DIGITAL_CERTIFICATE: {
    value: 'DIGITAL_CERTIFICATE',
    name: 'Certificado Digital',
    actions: ['DELETE'],
  },
  CalendarActivity: {
    value: 'CalendarActivity',
    name: 'Checklist',
    actions: ['CREATE', 'UPDATE', 'DELETE'],
  },
}

/** `Array.from(new Map(Object.entries(ENTITY_TYPE_CONSTANTS)).values())` in the legacy `_init()`. */
export const ENTITY_TYPES = Object.values(ENTITY_TYPE_CONSTANTS)

/**
 * Port of `onChangeEntityType()`'s body: given a raw `entityType` value,
 * returns the `{ value, name }` options for the dependent "Ações" select.
 * Returns an empty array for an empty/unknown `entityType`, same as the
 * legacy guard (`if (!$scope.type) return`, leaving `$scope.actions` as
 * whatever it was — which is only ever `[]` right after `_init()` or a
 * previous call to this same function).
 */
export function getActionsForEntityType(entityType) {
  const entity = ENTITY_TYPE_CONSTANTS[entityType]
  if (!entity) return []

  return entity.actions.map((action) => ({
    value: action,
    name: ACTIONS_CONSTANTS[action],
  }))
}

/** Port of `_formatData`'s `log.entityType = ENTITY_TYPE_CONSTANTS[log.entityType].name` line. */
export function getEntityTypeName(entityType) {
  return ENTITY_TYPE_CONSTANTS[entityType]?.name ?? entityType
}
